import type {
  GetAppointmentTimelineRequest,
  GetCampaignAnalysisTimeSeriesRequest,
  GetCampaignDeviceListRequest,
  GetDeviceEventStreamRequest,
  GetMarketingPerformanceAnalysisRequest,
} from "@/api/marketing-performance-analysis";
import type {
  GetAppointmentTimelineResponse,
  GetCampaignAnalysisTimeSeriesResponse,
  GetDeviceEventStreamResponse,
  GetDevicesForCampaignsResponse,
  MarketingPerformanceAnalysisCustomer,
  MarketingPerformanceAnalysisResponse,
} from "@/MarketingPerformanceAnalysis/types/MarketingPerformanceAnalysis";

import {
  getAdPerformanceAppointmentTimeline,
  getAdPerformanceCampaigns,
  getAdPerformanceCustomer,
  getAdPerformanceDeviceEvents,
  getAdPerformanceDevices,
  getAdPerformanceResults,
  getAdPerformanceTimeSeries,
  parseAdPerformanceDateRange,
} from "../adPerformance/dataset";
import { getPreviewProfile } from "../profiles";

export function handleGetMarketingPerformanceAnalysis(
  request: GetMarketingPerformanceAnalysisRequest,
): MarketingPerformanceAnalysisResponse {
  const profile = getPreviewProfile();
  const dateRange = parseAdPerformanceDateRange(request.dateRange);
  const results = getAdPerformanceResults(profile, dateRange);

  if (request.campaignID) {
    return results.filter((r) => r.campaignId === request.campaignID);
  }

  if (request.type) {
    const matchingCampaignIds = new Set(
      getAdPerformanceCampaigns(profile, dateRange)
        .filter((c) => c.platform === request.type)
        .map((c) => c.campaignId),
    );
    return results.filter((r) => matchingCampaignIds.has(r.campaignId));
  }

  return results;
}

export function handleGetMarketingPerformanceAnalysisCustomer(
  _request: Record<string, never>,
): MarketingPerformanceAnalysisCustomer {
  return getAdPerformanceCustomer(getPreviewProfile());
}

export function handleGetCampaignAnalysisTimeSeries(
  request: GetCampaignAnalysisTimeSeriesRequest,
): GetCampaignAnalysisTimeSeriesResponse {
  const profile = getPreviewProfile();
  const dateRange = parseAdPerformanceDateRange(request.dateRange);
  const campaign = getAdPerformanceResults(profile, dateRange).find(
    (c) => c.campaignId === request.campaignId,
  );
  return {
    campaignId: request.campaignId,
    campaignName: campaign?.campaignName ?? request.campaignId,
    metrics: getAdPerformanceTimeSeries(profile, request.campaignId, dateRange),
  };
}

export function handleGetCampaignDeviceList(
  request: GetCampaignDeviceListRequest,
): GetDevicesForCampaignsResponse {
  return getAdPerformanceDevices(
    getPreviewProfile(),
    request.campaignId,
    parseAdPerformanceDateRange(request.dateRange),
  );
}

export function handleGetDeviceEventStream(
  request: GetDeviceEventStreamRequest,
): GetDeviceEventStreamResponse {
  return { events: getAdPerformanceDeviceEvents(request.deviceId) };
}

export function handleGetAppointmentTimeline(
  request: GetAppointmentTimelineRequest,
): GetAppointmentTimelineResponse {
  return {
    appointments: getAdPerformanceAppointmentTimeline(
      getPreviewProfile(),
      request.campaignId,
    ),
  };
}
